import React, { useState, useEffect } from 'react';
import { db } from '../config/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

export default function HostGiftsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDetailedView, setShowDetailedView] = useState(false);

  useEffect(() => {
    const fetchHostGifts = async () => {
      try {
        setLoading(true);
        const productsRef = collection(db, 'products');
        
        // Query for products that contain "HstGft" in their Categories field
        const q = query(
          productsRef,
          where('Categories', '!=', null),
          where('isActive', '==', true)
        );
        
        const querySnapshot = await getDocs(q);
        const productList = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          // Check if Categories field contains "HstGft"
          if (data.Categories && data.Categories.includes('HstGft')) {
            productList.push({
              id: doc.id,
              ...data
            });
          }
        });
        
        setProducts(productList);
      } catch (error) {
        console.error('Error fetching host gifts:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHostGifts();
  }, []);

  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setShowDetailedView(true);
  };

  const closeDetailedView = () => {
    setShowDetailedView(false);
    setSelectedProduct(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7FBFC] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#033EA5] mx-auto"></div>
          <p className="mt-4 text-[#033EA5]">Loading host gifts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7FBFC] p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#011841] mb-2">Hosting Gifts</h1>
          <p className="text-[#033EA5] text-lg">Browse our collection of host appreciation gifts</p>
        </div>

        {/* Products Grid */}
        {products.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎁</div>
            <h3 className="text-xl font-semibold text-[#011841] mb-2">No Hosting Gifts Available</h3>
            <p className="text-[#033EA5]">Check back later for new host gift selections.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-xl shadow-lg border border-[#19BFB7]/20 overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-105"
                onClick={() => handleProductClick(product)}
              >
                {/* Product Image */}
                <div className="w-full h-48 bg-gray-50 relative overflow-hidden">
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `
                        <div class="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500">
                          <div class="text-center">
                            <div class="text-4xl mb-2">📦</div>
                            <div class="text-sm">No Image</div>
                          </div>
                        </div>
                      `;
                    }}
                  />
                </div>

                {/* Product Info */}
                <div className="p-4">
                  <h3 className="font-bold text-[#011841] text-lg mb-2 line-clamp-2">{product.name}</h3>
                  

                  {product.USP && (
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{product.USP}</p>
                  )}

                  <button className="w-full bg-[#19BFB7] hover:bg-[#28E3DA] text-white px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300">
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Detailed View Modal */}
        {showDetailedView && selectedProduct && (
          <DetailedProductView
            product={selectedProduct}
            onClose={closeDetailedView}
          />
        )}
      </div>
    </div>
  );
}

// DetailedProductView component for showing full product details
function DetailedProductView({ product, onClose }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#F7FBFC] rounded-xl shadow-2xl max-w-4xl max-h-[90vh] overflow-y-auto w-full border border-[#19BFB7]/20">
        <div className="flex justify-between items-center mb-6 p-6 border-b border-[#19BFB7]/20">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#011841]">{product.name}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-[#033EA5] hover:text-[#022b73] text-2xl font-bold p-2 hover:bg-[#E8F4F8] rounded-lg transition-all duration-300"
          >
            ×
          </button>
        </div>

        <div className="p-6">
          {/* Product Image */}
          <div className="w-full bg-gray-50 rounded-lg mb-6">
            <div className="w-full h-64 sm:h-96 flex items-center justify-center p-4">
              <img
                src={product.imageUrl}
                alt={product.name}
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.parentElement.style.display = 'none';
                  e.target.parentElement.nextElementSibling.style.display = 'flex';
                }}
              />
            </div>
            <div className="w-full h-64 sm:h-96 bg-gray-200 flex items-center justify-center text-gray-500" style={{display: 'none'}}>
              <div className="text-center">
                <div className="text-6xl mb-4">📦</div>
                <div className="text-xl">No Image Available</div>
              </div>
            </div>
          </div>

          {/* Product Details */}
          <div className="space-y-6">
            {product.USP && (
              <div>
                <h3 className="text-xl font-bold text-[#011841] mb-3">✨ Key Features</h3>
                <div className="bg-[#F7FBFC] rounded-lg p-4 border border-[#19BFB7]/20">
                  <p className="text-[#033EA5] whitespace-pre-line leading-relaxed">{product.USP}</p>
                </div>
              </div>
            )}

            {product.description && (
              <div>
                <h3 className="text-xl font-bold text-[#011841] mb-3">📝 Description</h3>
                <p className="text-gray-600 leading-relaxed">{product.description}</p>
              </div>
            )}

            {/* Additional Product Information */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white rounded-lg p-4 border border-[#19BFB7]/20">
              {product.code && (
                <div>
                  <span className="font-semibold text-[#011841]">Product Code:</span>
                  <span className="ml-2 text-[#033EA5]">{product.code}</span>
                </div>
              )}
              
              <div>
                <span className="font-semibold text-[#011841]">Status:</span>
                <span className={`ml-2 px-2 py-1 rounded text-sm ${
                  product.isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {product.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Action Button */}
          <div className="flex justify-end mt-8">
            <button
              onClick={onClose}
              className="px-6 py-3 bg-[#033EA5] hover:bg-[#022b73] text-white rounded-lg font-medium transition-all duration-300 shadow-md hover:shadow-lg"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
